/**
 * moduleStore — Vibe Designer Modules tab module-permitting (Lever 1).
 *
 * Single source of truth for WHICH modules the current tenant may load in
 * the Modules tab. Fetches `GET /api/modules/permitted` (JWT-gated; backend
 * intersects the tenant's enabled module ids with the host's MODULE_METADATA
 * and returns display metadata). The dropdown in ModuleHostPane lists the
 * intersection of this list with the frontend MODULE_REGISTRY (modules the
 * host actually knows how to render).
 *
 * `loaded` distinguishes "the gate explicitly returned a list" (honor it,
 * even if empty) from "we haven't heard back / it errored" (ModuleHostPane
 * degrades open to the full registry so a backend hiccup never hides a
 * working module — the module's own backend still enforces per-tenant auth).
 *
 * Auth: routes through services/api.ts `api.get()`, which attaches the
 * Bearer token from localStorage['vibe_designer_token'] automatically.
 */
import { create } from 'zustand';
import { api } from '../services/api';

/** One entry of `GET /api/modules/permitted` → `{ modules: [...] }`.
 * Shape mirrors the host backend's MODULE_METADATA composition
 * (routes/modules.py:get_permitted_modules). */
export interface PermittedModule {
  module_id: string;
  display_name: string;
  mount_point?: string;
  version?: string;
}

interface ModuleState {
  permittedModules: PermittedModule[];
  /** true once a permitted-modules fetch has SUCCEEDED at least once. While
   * false (initial / in-flight / errored), ModuleHostPane degrades open. */
  loaded: boolean;
  isLoading: boolean;
  error: Error | null;
  fetchPermittedModules: () => Promise<void>;
  reset: () => void;
}

export const useModuleStore = create<ModuleState>((set, get) => ({
  permittedModules: [],
  loaded: false,
  isLoading: false,
  error: null,

  fetchPermittedModules: async () => {
    // Fetch-once per session (mirrors tenantStore.fetchTenant): the permitted-
    // module list is stable for a session, so re-fetching would only re-render
    // Modules consumers for no gain. `loaded` flips true on success only, so a
    // failed first attempt can still be retried.
    if (get().loaded || get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{ modules: PermittedModule[]; catalog_source?: string }>('/modules/permitted');
      // M6: when the host's catalog DB is down with NO cache
      // (catalog_source === 'unavailable'), the backend may have dropped
      // catalog-only modules (those with no MODULE_METADATA fallback) from this
      // list. Leave `loaded` false so ModuleHostPane degrades OPEN to the full
      // registry rather than honoring a possibly-incomplete list — the module's
      // own backend still enforces per-tenant auth. 'live' and 'cache' (a served
      // last-known-good snapshot) are both complete, so they load normally.
      const catalogUnavailable = data?.catalog_source === 'unavailable';
      if (catalogUnavailable) {
        console.warn('[moduleStore] module catalog unavailable — degrading open to the full registry');
      }
      set({
        permittedModules: Array.isArray(data?.modules) ? data.modules : [],
        loaded: !catalogUnavailable,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      // Leave `loaded` false so the host degrades open rather than hiding
      // every module on a transient backend failure.
      set({ isLoading: false, error: err as Error });
    }
  },

  reset: () => set({ permittedModules: [], loaded: false, isLoading: false, error: null }),
}));
